/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useMemo, useRef, useState } from 'react';

import cn from 'classnames';

import styles from './ApiCodeBlock.module.scss';

import type { ApiCodeBlockProps, CodeToken, TokenType } from './ApiCodeBlock.types';

const INITIAL_DELAY_MS = 600;
const LOOP_PAUSE_MS = 2400;
const RESET_PAUSE_MS = 350;

function charDelay(char: string): number {
  if (char === '\n') {
    return 180 + Math.random() * 160;
  }

  if ('{}[]()'.includes(char)) {
    return 90 + Math.random() * 80;
  }

  if (',"\'`'.includes(char)) {
    return 70 + Math.random() * 60;
  }

  if (char === ' ') {
    return 45 + Math.random() * 35;
  }

  if (char === '/') {
    return 55 + Math.random() * 45;
  }

  return 50 + Math.random() * 60;
}

const TOKEN_CLASS: Partial<Record<TokenType, string>> = {
  comment: styles.tComment,
  method: styles.tMethod,
  path: styles.tPath,
  key: styles.tKey,
  string: styles.tString,
  bool: styles.tBool,
  score: styles.tScore,
};

interface FlatChar {
  char: string;
  type?: TokenType;
}

function flattenTokens(tokens: CodeToken[]): FlatChar[] {
  return tokens.flatMap(({ text, type }) => Array.from(text).map((char) => ({ char, type })));
}

function buildDisplayTokens(chars: FlatChar[]): Array<{ text: string; type?: TokenType }> {
  if (!chars.length) {
    return [];
  }

  const out: Array<{ text: string; type?: TokenType }> = [];
  let text = chars[0].char;
  let type = chars[0].type;

  for (let i = 1; i < chars.length; i++) {
    if (chars[i].type === type) {
      text += chars[i].char;
    } else {
      out.push({ text, type });
      text = chars[i].char;
      type = chars[i].type;
    }
  }

  out.push({ text, type });
  return out;
}

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <circle cx="8" cy="8" r="8" fill="#36b38f" />
    <path d="M4.5 8L6.8 10.3L11.5 5.7" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PendingIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <circle cx="8" cy="8" r="7" fill="#dfe4ec" stroke="#042354" strokeWidth="1" />
  </svg>
);

export const ApiCodeBlock = ({ filename, codeTokens, results, animated = false, className }: ApiCodeBlockProps) => {
  const flatChars = useMemo(() => flattenTokens(codeTokens), [codeTokens]);
  const totalChars = flatChars.length;

  const [visibleCount, setVisibleCount] = useState(animated ? 0 : totalChars);

  const activeRef = useRef(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const flatCharsRef = useRef(flatChars);
  flatCharsRef.current = flatChars;

  useEffect(() => {
    if (!animated) {
      setVisibleCount(totalChars);
      return;
    }

    activeRef.current = true;

    const typeChar = (index: number) => {
      if (!activeRef.current) {
        return;
      }

      setVisibleCount(index);

      if (index < totalChars) {
        const char = flatCharsRef.current[index]?.char ?? '';
        timerRef.current = setTimeout(() => typeChar(index + 1), charDelay(char));
      } else {
        timerRef.current = setTimeout(() => {
          if (!activeRef.current) {
            return;
          }

          setVisibleCount(0);
          timerRef.current = setTimeout(() => {
            if (activeRef.current) typeChar(0);
          }, RESET_PAUSE_MS);
        }, LOOP_PAUSE_MS);
      }
    };

    timerRef.current = setTimeout(() => typeChar(0), INITIAL_DELAY_MS);

    return () => {
      activeRef.current = false;

      if (timerRef.current !== undefined) {
        clearTimeout(timerRef.current);
      }
    };
  }, [animated, totalChars]);

  const sizerTokens = useMemo(() => buildDisplayTokens(flatChars), [flatChars]);
  const displayTokens = useMemo(() => buildDisplayTokens(flatChars.slice(0, visibleCount)), [flatChars, visibleCount]);

  const isTyping = animated && visibleCount < totalChars;

  const renderTokens = (tokens: ReturnType<typeof buildDisplayTokens>) =>
    tokens.map((token, i) =>
      token.type ? (
        <span key={i} className={TOKEN_CLASS[token.type]}>
          {token.text}
        </span>
      ) : (
        <span key={i}>{token.text}</span>
      ),
    );

  return (
    <div className={cn(styles.root, className)}>
      <div className={styles.header}>
        <div className={styles.dots}>
          <span className={styles.dot} />
          <span className={styles.dot} />
          <span className={styles.dot} />
        </div>

        <span className={styles.filename}>{filename}</span>
      </div>

      <div className={styles.body}>
        <div className={styles.codeWrapper}>
          <pre className={cn(styles.code, styles.codeSizer)} aria-hidden>
            {renderTokens(sizerTokens)}
          </pre>

          <pre className={cn(styles.code, styles.codeVisible)}>
            {renderTokens(displayTokens)}
            {isTyping && <span className={styles.cursor} aria-hidden />}
          </pre>
        </div>

        {results && results.length > 0 && (
          <div className={styles.results}>
            {results.map((item, i) => (
              <div key={i} className={styles.resultItem}>
                <span className={styles.resultIcon}>{item.status === 'success' ? <CheckIcon /> : <PendingIcon />}</span>
                <span className={styles.resultContent}>{item.content}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
